import {
  Component,
  OnInit,
  AfterViewInit,
  PLATFORM_ID,
  ChangeDetectorRef,
  NgZone,
} from '@angular/core';
import { DestroyRef, inject } from '@angular/core';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { State } from '../../shared/core/state';

import * as UserActions from '../../shared/core/state/users/user.actions';
import { UserRoles, UserStatus } from '../../shared/core/state/users/models';
import {
  confirmPasswordValidator,
  createGoogleOnboardingForm,
  emailPhoneValidator,
  hasFieldError,
  isFieldInvalid,
  isUnderAge,
} from '../../shared/util/auth.validators';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../shared/services/auth.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { isPlatformServer } from '@angular/common';
import { ToastService } from '@black/ui';
import { PageLoaderService } from '../../shared/util/pageloader.service';
import { TenantService } from '../../shared/services/tenant.service';
import { InvitationService } from '../../shared/services/invitation.service';
import { finalize } from 'rxjs';

declare const google: any;

type RegistrationStep = 'identify' | 'verify' | 'complete';

@Component({
  selector: 'black-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.css'],
  standalone: false,
  host: {
    ngSkipHydration: 'true',
  },
})
export class SignupComponent implements OnInit, AfterViewInit {
  private fb = inject(NonNullableFormBuilder);
  private store = inject<Store<State>>(Store);
  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);
  private http = inject(HttpClient);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  private toastService = inject(ToastService);
  private pageLoaderService = inject(PageLoaderService);
  private ngZone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);
  private tenantService = inject(TenantService);
  private invitationService = inject(InvitationService);

  // Registration flow control
  currentStep: RegistrationStep = 'identify';
  registerAsInstructor = false;
  inviteToken: string | null = null;
  error: string | null = null;
  isLoading = false;
  isGoogleLoading = false;
  isServer: boolean;
  showPassword = false;
  showConfirmPassword = false;

  // Google Onboarding Modal Control
  showGoogleOnboarding = false;
  googleIdToken: string | null = null;
  googleRole: string | null = null;
  googleOnboardingForm: FormGroup;

  // Step 1: Identify Form
  identifyForm: FormGroup;

  registrationToken: string | null = null;

  // Step 3: Complete Registration Form
  completeForm: FormGroup;

  private destroyRef = inject(DestroyRef);

  constructor() {
    // Step 1: Identify form - Username only and Agree checkbox
    this.identifyForm = this.fb.group({
      username: [
        '',
        { validators: [Validators.required, emailPhoneValidator] },
      ],
      agree: [false, Validators.requiredTrue],
    });

    // Step 3: Complete form - Password and profile info and Agree checkbox
    this.completeForm = this.fb.group(
      {
        first_name: ['', Validators.required],
        last_name: ['', Validators.required],
        dateOfBirth: ['', Validators.required],
        parentEmail: ['', Validators.email],
        password: [
          '',
          {
            validators: [
              Validators.required,
              Validators.pattern('(?=.*[a-z])(?=.*[A-Z])(?=.*\\d).{8,}'),
            ],
          },
        ],
        confirm_password: ['', Validators.required],
        agree: [false, Validators.requiredTrue],
      },
      { validators: confirmPasswordValidator },
    );

    this.completeForm
      .get('dateOfBirth')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((dobValue) => {
        if (dobValue) {
          const dobControl = this.completeForm.get('dateOfBirth');
          if (this.registerAsInstructor && isUnderAge(dobValue, 16)) {
            dobControl?.setErrors({ instructorUnder16: true });
          } else if (dobControl?.hasError('instructorUnder16')) {
            const errors = { ...dobControl.errors };
            delete errors['instructorUnder16'];
            dobControl.setErrors(Object.keys(errors).length ? errors : null);
          }

          const parentEmailControl = this.completeForm.get('parentEmail');
          if (!this.registerAsInstructor && isUnderAge(dobValue, 16)) {
            parentEmailControl?.setValidators([
              Validators.required,
              Validators.email,
            ]);
          } else {
            parentEmailControl?.setValidators([Validators.email]);
          }
          queueMicrotask(() => {
            parentEmailControl?.updateValueAndValidity();
            this.cdr.markForCheck();
          });
        }
      });

    // Google Onboarding Form
    this.googleOnboardingForm = createGoogleOnboardingForm(
      this.fb,
      this.destroyRef,
      this.cdr
    );

    this.isServer = isPlatformServer(this.platformId);
  }

  ngAfterViewInit(): void {
    // Only initialize Google Sign-In on client side and on identify step
    if (
      !this.isServer &&
      typeof google !== 'undefined' &&
      this.currentStep === 'identify'
    ) {
      setTimeout(() => {
        this.initializeGoogleSignIn();
      }, 0);
    }
  }

  get isGoogleLoginEnabled(): boolean {
    return this.tenantService.isGoogleLoginEnabled();
  }

  initializeGoogleSignIn(): void {
    if (!this.isGoogleLoginEnabled) {
      return;
    }

    const activeGoogleClientId = this.tenantService.getGoogleClientId();

    // Skip initialization if Google Client ID is not configured
    if (
      !activeGoogleClientId ||
      activeGoogleClientId.includes('YOUR_GOOGLE_CLIENT_ID')
    ) {
      console.warn(
        'Google Client ID not configured. Google Sign-In is disabled. See GOOGLE_SIGNIN_INTEGRATION.md for setup.',
      );
      return;
    }

    try {
      google.accounts.id.initialize({
        client_id: activeGoogleClientId,
        callback: (response: any) =>
          this.ngZone.run(() => this.handleGoogleSignIn(response)),
        ux_mode: 'popup',
        itp_support: true,
      });

      const buttonContainer = document.getElementById('google-signup-button');
      if (buttonContainer) {
        const containerWidth = buttonContainer.offsetWidth || 384;
        google.accounts.id.renderButton(buttonContainer, {
          theme: 'filled_blue',
          size: 'large',
          text: 'signup_with',
          width: Math.min(containerWidth, 384),
        });
      }
    } catch (error) {
      console.error('Error initializing Google Sign-In:', error);
    }
  }

  handleGoogleSignIn(response: any): void {
    if (!this.identifyForm.get('agree')?.value) {
      this.error =
        'You must agree to the Terms and Conditions and Privacy Policy to continue.';
      return;
    }

    const idToken = response.credential;
    const role = this.registerAsInstructor
      ? UserRoles.INSTRUCTOR
      : UserRoles.STUDENT;

    this.isGoogleLoading = true;
    this.error = null;
    this.pageLoaderService.open();

    // Send to backend for authentication
    this.http
      .post(`${environment.host}/api/v1/auth/google-signin`, { idToken, role })
      .pipe(
        finalize(() => {
          this.isGoogleLoading = false;
          this.pageLoaderService.close();
        }),
      )
      .subscribe({
        next: (result: any) => {
          if (result.needsOnboarding) {
            this.googleIdToken = idToken;
            this.googleRole = role;
            this.showGoogleOnboarding = true;
          } else if (result.status === UserStatus.PENDING_PARENTAL_CONSENT) {
            this.toastService.showToast(
              result.message || 'Account pending parent/guardian consent.',
              'info',
            );
            this.router.navigate(['/auth/pending-consent']);
          } else if (result.user && result.token) {
            // Dispatch login success action
            this.store.dispatch(
              UserActions.loginSuccess({
                response: {
                  user: result.user,
                  token: result.token,
                  refreshToken: result.refreshToken,
                },
              }),
            );

            // Navigate to dashboard
            this.router.navigate(['/dashboard']);
          }
        },
        error: (error: any) => {
          console.error('Google sign-in failed:', error);
          const msg =
            error.error?.message || 'Google sign-in failed. Please try again.';
          this.error = msg;
          this.toastService.showToast(msg, 'error');
        },
      });
  }

  submitGoogleOnboarding(): void {
    const dateOfBirth = this.googleOnboardingForm.value.dateOfBirth;
    if (this.googleRole === UserRoles.INSTRUCTOR && isUnderAge(dateOfBirth, 16)) {
      this.googleOnboardingForm.get('dateOfBirth')?.setErrors({ instructorUnder16: true });
      this.googleOnboardingForm.markAllAsTouched();
      const msg = 'Instructors must be at least 16 years of age.';
      this.error = msg;
      this.toastService.showToast(msg, 'error');
      return;
    }

    if (!this.googleOnboardingForm.valid || !this.googleIdToken) {
      this.googleOnboardingForm.markAllAsTouched();
      return;
    }

    this.isGoogleLoading = true;
    this.error = null;
    this.pageLoaderService.open();

    const parentEmail = this.googleOnboardingForm.value.parentEmail;

    this.http
      .post(`${environment.host}/api/v1/auth/google-signin`, {
        idToken: this.googleIdToken,
        role: this.googleRole,
        dateOfBirth,
        parentEmail,
      })
      .pipe(
        finalize(() => {
          this.isGoogleLoading = false;
          this.pageLoaderService.close();
        }),
      )
      .subscribe({
        next: (result: any) => {
          this.showGoogleOnboarding = false;
          if (result.status === UserStatus.PENDING_PARENTAL_CONSENT) {
            this.toastService.showToast(
              result.message ||
                'Account created. Pending parent/guardian consent.',
              'info',
            );
            this.router.navigate(['/auth/pending-consent']);
          } else if (result.user && result.token) {
            this.store.dispatch(
              UserActions.loginSuccess({
                response: {
                  user: result.user,
                  token: result.token,
                  refreshToken: result.refreshToken,
                },
              }),
            );
            this.router.navigate(['/dashboard']);
          }
        },
        error: (error: any) => {
          console.error('Google sign-in onboarding failed:', error);
          const msg =
            error.error?.message || 'Google sign-in failed. Please try again.';
          this.error = msg;
          this.toastService.showToast(msg, 'error');
        },
      });
  }

  get isGoogleUnder16(): boolean {
    return isUnderAge(this.googleOnboardingForm.get('dateOfBirth')?.value, 16);
  }

  ngOnInit(): void {
    if (!this.isServer) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
    this.store.dispatch(UserActions.clearUserState());

    this.authService.isAuthenticated().then((isAuth) => {
      if (isAuth) {
        this.store.dispatch(UserActions.logout({ redirect: false }));
      }
    });

    this.store
      .select((state) => state.users.error)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((msg) => {
        this.error = msg;
        this.isLoading = false;
        if (msg) {
          this.toastService.showToast(msg, 'error');
        }
      });

    this.store
      .select((state) => state.users.otp.expiresOn)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((expiresOn) => {
        if (expiresOn) {
          this.currentStep = 'verify';
          this.isLoading = false;
          if (!this.isServer) {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
          }
        } else if (this.currentStep === 'verify') {
          // User navigated back to identify step
          this.currentStep = 'identify';
          if (!this.isServer) {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
          }
          // Re-initialize Google Sign-In button after view update
          setTimeout(() => {
            if (!this.isServer && typeof google !== 'undefined') {
              this.initializeGoogleSignIn();
            }
          }, 100);
        }
      });

    this.store
      .select((state) => state.users.registrationToken)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((token) => {
        if (token) {
          this.registrationToken = token;
          this.currentStep = 'complete';
          this.isLoading = false;
          if (!this.isServer) {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
          }
        }
      });

    this.route.queryParamMap.subscribe((params) => {
      const userRoleFromQuery = params.get('role');
      const inviteToken = params.get('invite');

      if (inviteToken) {
        this.inviteToken = inviteToken;
        this.invitationService.getInvitationByToken(inviteToken).subscribe({
          next: (invitation) => {
            if (invitation.recipient) {
              this.identifyForm.patchValue({ username: invitation.recipient });
            }
            if (invitation.studentName) {
              this.completeForm.patchValue({ first_name: invitation.studentName });
            }
            if (invitation.role === UserRoles.INSTRUCTOR) {
              this.registerAsInstructor = true;
            }
          },
          error: () => {
            // Non-blocking if token is invalid or expired
          },
        });
      }

      if (
        userRoleFromQuery &&
        userRoleFromQuery.toUpperCase() === UserRoles.INSTRUCTOR
      ) {
        this.registerAsInstructor = true;
      } else if (!inviteToken) {
        this.registerAsInstructor = false;
      }
    });

    // Auto-accept invitation when user logs in / registers successfully
    this.store
      .select((state) => state.users.user)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        if (user && this.inviteToken) {
          const tokenToAccept = this.inviteToken;
          this.inviteToken = null;
          this.invitationService.acceptInvitation(tokenToAccept).subscribe({
            next: () => {
              // Invitation accepted successfully
            },
          });
        }
      });
  }

  // ====== STEP 1: Identify User & Send OTP ======
  submitIdentifyForm(): void {
    const usernameControl = this.identifyForm.get('username');
    usernameControl?.setValue(usernameControl.value.trim());

    if (!this.identifyForm.valid) {
      this.identifyForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.error = null;

    this.store.dispatch(
      UserActions.registerIdentify({ username: usernameControl?.value }),
    );
  }

  // ====== STEP 2: Verify OTP & Get Registration Token ======
  submitVerifyForm(code?: string): void {
    const trimmedCode = code?.trim();
    if (!trimmedCode) {
      this.error = 'Verification code is required.';
      return;
    }

    this.isLoading = true;
    this.error = null;

    const username = this.identifyForm.value.username;
    this.store.dispatch(
      UserActions.registerVerify({ username, code: trimmedCode }),
    );
  }

  // ====== STEP 3: Complete Registration ======
  submitCompleteForm(): void {
    const dobValue = this.completeForm.value.dateOfBirth;
    if (this.registerAsInstructor && isUnderAge(dobValue, 16)) {
      this.completeForm.get('dateOfBirth')?.setErrors({ instructorUnder16: true });
      this.completeForm.markAllAsTouched();
      const msg = 'Instructors must be at least 16 years of age.';
      this.error = msg;
      this.toastService.showToast(msg, 'error');
      return;
    }

    if (!this.completeForm.valid) {
      this.completeForm.markAllAsTouched();
      return;
    }

    if (!this.registrationToken) {
      this.error = 'Verification token missing. Please restart signup.';
      this.currentStep = 'identify';
      if (!this.isServer) {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      }
      return;
    }

    this.isLoading = true;
    this.error = null;

    const completePayload = {
      token: this.registrationToken,
      password: this.completeForm.value.password,
      firstName: this.completeForm.value.first_name,
      lastName: this.completeForm.value.last_name,
      dateOfBirth: this.completeForm.value.dateOfBirth,
      parentEmail: this.completeForm.value.parentEmail,
      role: this.registerAsInstructor
        ? UserRoles.INSTRUCTOR
        : UserRoles.STUDENT,
      inviteToken: this.inviteToken || undefined,
    };

    this.store.dispatch(UserActions.registerComplete(completePayload));
  }

  // ====== Helper Methods ======
  goToPreviousStep(): void {
    if (this.currentStep === 'verify') {
      this.currentStep = 'identify';
    } else if (this.currentStep === 'complete') {
      this.currentStep = 'verify';
    }
    this.error = null;
    if (!this.isServer) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }

  // ====== Validation Helper Methods ======
  hasFieldError(
    form: FormGroup,
    fieldName: string,
    errorType: string,
  ): boolean {
    return hasFieldError(form, fieldName, errorType);
  }

  isFieldInvalid(form: FormGroup, fieldName: string): boolean {
    return isFieldInvalid(form, fieldName);
  }

  hasFormError(form: FormGroup, errorType: string): boolean {
    return form.hasError(errorType) || false;
  }

  get isUnder16(): boolean {
    return isUnderAge(this.completeForm.get('dateOfBirth')?.value, 16);
  }
}
